import { directionAtom } from '@fe-core';
import { publishAnalyticsEvent } from '@utils/publishAnalyticsEvent';
import { useAtomValue } from 'jotai/index';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import {
  Pagination,
  PaginationProps,
} from '@fe-core-ui/src/components/Pagination/Pagination';
import { TW_ROOT } from '@utils/constants';
import {
  DEFAULT_PAGINATION_PAGE_ITEMS,
  PaginationPageSize,
  PaginationPageSizeItem,
} from '@fe-core-ui/src/components/Pagination/PageSizeSelect';

type _PaginationProps = Omit<
  PaginationProps,
  'dir' | 'onPreviousPage' | 'onNextPage' | 'texts' | 'pageSizeOptions'
>;
type ContextPaginationProps = _PaginationProps & {
  actionContext: 'Instructors' | 'Participants' | 'Events';
  texts: {
    ariaLabel: string;
  };
  pageSizeOptions: {
    pageSize: PaginationPageSize;
    onPageSizeChange: (pageSize: PaginationPageSize) => void;
    options?: PaginationPageSizeItem[];
  };
};

export const usePaginationPageSizes = (
  preOptions?: PaginationPageSizeItem[],
) => {
  const intl = useIntl();
  return [
    ...(preOptions || []),
    ...DEFAULT_PAGINATION_PAGE_ITEMS,
    {
      key: 'all' as const,
      label: intl.formatMessage({
        id: 'learner.common.pagination.size.all',
      }),
    },
  ];
};

export const ContextPagination: FC<ContextPaginationProps> = ({
  page,
  totalNoItems,
  pageSizeOptions,
  setPageParams,
  actionContext,
  texts,
  ...rest
}) => {
  const intl = useIntl();
  const dir = useAtomValue(directionAtom);
  const options = usePaginationPageSizes();

  const extendedPageSizeOptions = {
    options,
    ...pageSizeOptions,
    label: intl.formatMessage({
      id: 'learner.common.pagination.size.label',
    }),
    container: document.getElementById(TW_ROOT) as HTMLElement,
  };

  return (
    <Pagination
      {...rest}
      dir={dir}
      page={page}
      totalNoItems={totalNoItems}
      pageSizeOptions={extendedPageSizeOptions}
      setPageParams={setPageParams}
      onPreviousPage={(pageIndex) => {
        publishAnalyticsEvent(
          {
            context: 'CatalogDetailPage',
            action: `${actionContext}PaginationPreviousClick`,
          },
          {
            page: pageIndex,
          },
        );
      }}
      onNextPage={(pageIndex) => {
        publishAnalyticsEvent(
          {
            context: 'CatalogDetailPage',
            action: `${actionContext}PaginationNextClick`,
          },
          {
            page: pageIndex,
          },
        );
      }}
      onPageChange={(pageIndex) => {
        rest.onPageChange?.(pageIndex);
        publishAnalyticsEvent(
          {
            context: 'CatalogDetailPage',
            action: `${actionContext}PaginationPageClick`,
          },
          {
            page: pageIndex + 1,
          },
        );
      }}
      texts={{
        ariaLabel: texts.ariaLabel,

        ariaTitle: intl.formatMessage({
          id: 'learner.common.pagination.ariaTitle',
        }),
        pageAriaLabel: intl.formatMessage({
          id: 'learner.common.pagination.aria.page',
        }),
        selectedPageAriaLabel: intl.formatMessage({
          id: 'learner.common.pagination.aria.selectedPage',
        }),
        next: intl.formatMessage({
          id: 'learner.common.pagination.next',
        }),
        prev: intl.formatMessage({
          id: 'learner.common.pagination.prev',
        }),
      }}
    />
  );
};
